const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const REVISION = '2026-07-15';

class KlaviyoBackInStock extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();

    this.#button?.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get #button() {
    return this.querySelector('.klaviyo-bis__button');
  }

  get #message() {
    return this.querySelector('.klaviyo-bis__message');
  }

  #onClick = async () => {
    const { apiKey, email, catalogVariantId } = this.dataset;

    if (!apiKey) {
      console.warn('[klaviyo-back-in-stock] No public API key set. Add it in Theme settings → Klaviyo.');
      this.#setMessage(this.dataset.unconfiguredMessage, 'error');
      return;
    }

    if (!email) return;

    if (!catalogVariantId) {
      console.warn('[klaviyo-back-in-stock] No catalog variant id on the element.');
      this.#setMessage(this.dataset.errorMessage, 'error');
      return;
    }

    this.#setBusy(true);
    this.#hideMessage();

    try {
      const body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: catalogVariantId } },
          },
        },
      };

      const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(apiKey)}`, {
        method: 'POST',
        headers: { revision: REVISION, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const detail = payload.errors?.[0]?.detail;

        throw new Error(detail ?? `Klaviyo responded with ${response.status}`);
      }

      if (!this.isConnected) return;

      this.#setMessage(this.dataset.successMessage, 'success');
      this.#button?.setAttribute('hidden', '');
    } catch (error) {
      console.warn('[klaviyo-back-in-stock]', catalogVariantId, error);

      if (this.isConnected) this.#setMessage(this.dataset.errorMessage, 'error');
    } finally {
      if (this.isConnected) this.#setBusy(false);
    }
  };

  #setBusy(busy) {
    const button = this.#button;

    if (!button) return;

    button.disabled = busy;

    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  #setMessage(text, status) {
    const message = this.#message;

    if (!message || !text) return;

    message.classList.toggle('klaviyo-bis__message--success', status === 'success');
    message.classList.toggle('klaviyo-bis__message--error', status === 'error');
    message.textContent = text;
    message.hidden = false;
  }

  #hideMessage() {
    const message = this.#message;

    if (message) message.hidden = true;
  }
}

if (!window.customElements.get('klaviyo-back-in-stock')) {
  window.customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
}
